import { ClientEvents, MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import { getMealPlanServingLabel, isMealPlanMain } from 'common/MealPlanUtils';
import React, { useMemo } from 'react';
import {
  MealPlanDisplayFoodItemFragment,
  MealPlanShefFragment,
  useMealPlanDishCardDataQuery,
} from 'src/gqlReactTypings.generated.d';
import { getDisplayServingsForDish } from 'src/pages/consumer/meal-plans/utils/getDisplayServingsForDish';
import { isMainSelectionCapped } from 'src/pages/consumer/meal-plans/utils/isMainSelectionCapped';
import { useIsFlexServingsV3 } from 'src/shared/hooks/experiments/useIsFlexServingsV3';
import { useIsUserInFlexServings } from 'src/shared/hooks/experiments/useIsUserInFlexServings';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCartStore, useCartStoreSelectors } from 'src/ztore/cart-store';
import { useTasteProfileFiltersStoreSelectors } from 'src/ztore/taste-profile-filters-store';
import { useFoodItemAvailability } from 'src/ztore/wss/hooks/useFoodItemAvailability';
import { useMealPlanCatalogLookup } from 'src/ztore/wss/hooks/useMealPlanCatalog';
import { useTasteProfileForUser } from 'src/ztore/wss/hooks/useTasteProfileForUser';
import { useMealPlanCatalogStore } from 'src/ztore/wss/meal-plan-catalog-store';
import Chip, { ChipColor } from 'web-common/src/shared/design-system/components/Chip';
import { FlexServingsQuantityPicker } from '../../cart/FlexServingsQuantityPicker';
import { MealPlanQuantityPicker } from '../../checkout/MealPlanQuantityPicker';
import { DishCardDisplayMode } from '../../checkout/types';
import { useCartActionsWithAnalytics } from '../../hooks/useCartActionsWithAnalytics';
import { useDishDetailsModal } from '../../hooks/useDishDetailsModal';
import { useMealPlanDeliveryInfo } from '../../hooks/useMealPlanDeliveryInfo';
import WrapperMealPlanDishModal from '../MealPlanDishModal';
import { MealPlanDishCardLarge } from './MealPlanDishCardLarge';
import { MealPlanDishCardSmall } from './MealPlanDishCardSmall';

export enum CardType {
  Large = 'Large',
  LargeWithShefName = 'LargeWithShefName',
  Small = 'Small',
  SmallWithShefName = 'SmallWithShefName',
}

export const ServingChip: React.FC<{ numServings: number; mealPlanFoodType?: string | null }> = ({
  numServings,
  mealPlanFoodType,
}) => (
  <Chip color={ChipColor.CANTELOUPE_50} copy={getMealPlanServingLabel({ foodType: mealPlanFoodType, numServings })} />
);

export const PriceChip: React.FC<{ price: number; optionalText?: string }> = ({ price, optionalText = '' }) => {
  const copy = `$${price / 100} ${optionalText}`;
  return <Chip color={ChipColor.MINT_50} copy={copy} />;
};

interface BaseDishCardProps {
  cardType: CardType;
  displayMode?: DishCardDisplayMode;
  foodItemIndex: number;
  hideServingAndPricing?: boolean;
  limitDishSelection?: boolean;
  isCartEnabled?: boolean;
  shefIndex?: number;
  analyticsLocation: MealPlanAnalyticsLocation;
  collectionId?: string;
  quantity?: number;
  numServings?: number; // used when isCartEnabled is false
  isDishModalEnabled?: boolean;
  searchQuery?: string;
  showNewFreshTag?: boolean;
  favoriteHeart?: React.ReactElement;
}

// Internal component that receives the actual data objects
interface IMealPlanDishCardComponentProps extends BaseDishCardProps {
  shef: MealPlanShefFragment;
  foodItem: MealPlanDisplayFoodItemFragment;
  isCartEnabled: boolean;
}

const MealPlanDishCardComponent: React.FC<IMealPlanDishCardComponentProps> = ({
  cardType,
  displayMode = DishCardDisplayMode.Carousel,
  foodItem,
  foodItemIndex,
  hideServingAndPricing,
  limitDishSelection = true,
  isCartEnabled = true,
  shefIndex,
  shef,
  analyticsLocation,
  collectionId,
  quantity = 0,
  numServings: numServingsProp,
  isDishModalEnabled = true,
  searchQuery,
  showNewFreshTag,
  favoriteHeart,
}) => {
  const { getNumRemainingMains, numServings: storeNumServings } = useCartStoreSelectors();
  const numServings = (isCartEnabled ? storeNumServings : numServingsProp) ?? 1;
  const { upsell } = foodItem;
  const isFlexServings = useIsUserInFlexServings();
  const isFlexServingsV3 = useIsFlexServingsV3();
  const showFlexServingsStepper = isFlexServings && !isFlexServingsV3 && numServings !== 1;

  const { numAvailableUnits, numUnitsInCart, numAvailableServings, numServingsInCart } = useFoodItemAvailability(
    foodItem,
    shef
  );

  const foodItemIsMain = isMealPlanMain(foodItem);
  const selectedAllDishesForWeek = isMainSelectionCapped({
    isFlexServings,
    foodItemIsMain,
    numRemainingMains: getNumRemainingMains(),
  });
  const itemUnavailable = isFlexServings ? numAvailableServings <= 0 : numAvailableUnits <= 0;

  const displayServings = getDisplayServingsForDish({
    mealPlanFoodType: foodItem.mealPlanFoodType,
    numServings,
    isFlexServings,
  });
  const buttonsDisabled = useMemo(
    () => (itemUnavailable || limitDishSelection ? selectedAllDishesForWeek || itemUnavailable : false),
    [selectedAllDishesForWeek, itemUnavailable, limitDishSelection]
  );

  const {
    isOpen: isDishDetailsModalOpen,
    open: openDishDetailsModal,
    close: closeDishDetailsModal,
  } = useDishDetailsModal();

  const tracker = useTracker();
  const openDishModal = () => {
    if (isDishModalEnabled) {
      if (searchQuery) {
        tracker.track(ClientEvents.MEAL_PLAN_SEARCH_RESULT_CLICK, {
          query: searchQuery,
          foodItemId: foodItem.id,
          foodItemName: foodItem.name ?? null,
          shefId: shef.id,
          foodItemType: foodItemIsMain ? 'mains' : 'sides',
        });
      }
      openDishDetailsModal();
    }
  };

  const isLargeCard = [CardType.Large, CardType.LargeWithShefName].includes(cardType);

  const { incrementInCart, decrementInCart } = useCartActionsWithAnalytics({
    foodItem,
    shef,
    analyticsLocation,
    foodItemPosition: foodItemIndex,
    shefPosition: shefIndex,
    collectionId,
  });

  const handleCardOnClick = isDishModalEnabled ? openDishModal : undefined;
  const quantityToUse = isCartEnabled ? numUnitsInCart : quantity;
  const onIncrement = isCartEnabled ? incrementInCart : undefined;
  const onDecrement = isCartEnabled ? decrementInCart : undefined;

  const cardQuantity = isFlexServings && isCartEnabled ? numServingsInCart : quantityToUse;
  const availableForCard = isFlexServings ? numAvailableServings : numAvailableUnits;
  const cardRemainingAvailable = isCartEnabled ? availableForCard : Infinity;

  const activeQuantityPicker = showFlexServingsStepper ? (
    <FlexServingsQuantityPicker
      quantity={numServingsInCart}
      disabled={buttonsDisabled}
      onIncrement={incrementInCart}
      onDecrement={decrementInCart}
    />
  ) : (
    <MealPlanQuantityPicker
      outline={false}
      value={isFlexServings ? numServingsInCart : numUnitsInCart}
      disabled={buttonsDisabled}
      onIncrement={incrementInCart}
      onDecrement={decrementInCart}
    />
  );
  const quantityPicker = isCartEnabled ? activeQuantityPicker : undefined;

  const largeCard = (
    <MealPlanDishCardLarge
      displayMode={displayMode}
      foodItem={foodItem}
      showShef={cardType === CardType.LargeWithShefName}
      displayServings={displayServings}
      hideServingAndPricing={hideServingAndPricing}
      isDishModalEnabled={isDishModalEnabled}
      remainingAvailableUnits={cardRemainingAvailable}
      quantity={cardQuantity}
      quantityPicker={quantityPicker}
      showFlexServingsStepper={showFlexServingsStepper}
      servingsInCart={numServingsInCart}
      onClick={handleCardOnClick}
      showNewFreshTag={showNewFreshTag}
      favoriteHeart={favoriteHeart}
    />
  );

  const smallCard = (
    <MealPlanDishCardSmall
      displayMode={displayMode}
      foodItem={foodItem}
      showShef={cardType === CardType.SmallWithShefName}
      hideServingAndPricing={hideServingAndPricing}
      remainingAvailableUnits={cardRemainingAvailable}
      displayServings={displayServings}
      quantity={cardQuantity}
      onClick={handleCardOnClick}
      buttonsDisabled={buttonsDisabled}
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      showFlexServingsStepper={showFlexServingsStepper}
      quantityPicker={quantityPicker}
      showNewFreshTag={showNewFreshTag}
      favoriteHeart={favoriteHeart}
    />
  );

  const cardToRender = isLargeCard ? largeCard : smallCard;

  return (
    <>
      {cardToRender}
      {isDishDetailsModalOpen && (
        <WrapperMealPlanDishModal
          disabled={buttonsDisabled}
          foodItemId={foodItem.id}
          shefId={shef.id}
          isShowing={isDishDetailsModalOpen}
          showModalPrompt={false}
          cardType={cardType}
          upsell={upsell}
          onClose={closeDishDetailsModal}
          foodItemIndex={foodItemIndex}
          shefIndex={shefIndex}
          openModal={openDishModal}
          analyticsLocation={analyticsLocation}
          showAddButton={isCartEnabled}
          collectionId={collectionId}
        />
      )}
    </>
  );
};

interface IMealPlanDishCardProps extends BaseDishCardProps {
  foodItemId: number;
  shefId: string;
  isCartEnabled: boolean;
}

export const MealPlanDishCard: React.FC<IMealPlanDishCardProps> = (props) => {
  const location = useLocation();
  const { tasteProfile } = useTasteProfileForUser();
  const hasKids = (tasteProfile?.numLittleEaters ?? 0) + (tasteProfile?.numBigEaters ?? 0) > 0;
  const isOnCheckout = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_CHECKOUT]);
  const { regionId } = useMealPlanDeliveryInfo();

  const { shefId, foodItemId } = props;
  const deliveryDate = useCartStore((state) => state.deliveryDate);
  const { getFilters } = useTasteProfileFiltersStoreSelectors();
  const { replacementDietaryRestrictions: filtersRestrictions } = getFilters();
  const replacementDietaryRestrictions = filtersRestrictions?.length
    ? filtersRestrictions
    : tasteProfile?.replacementDietaryRestrictions ?? [];

  const { foodItem: cachedFoodItem, shef: cachedShef } = useMealPlanCatalogLookup(foodItemId, shefId);
  const setFoodItem = useMealPlanCatalogStore((s) => s.setFoodItem);
  const setShef = useMealPlanCatalogStore((s) => s.setShef);

  const { data, loading } = useMealPlanDishCardDataQuery({
    variables: {
      foodItemId,
      shefId,
      deliveryDate,
      replacementDietaryRestrictions,
      hasKids,
      isOnCheckout,
      regionId,
    },
    fetchPolicy: 'cache-first',
    skip: !!(cachedFoodItem && cachedShef),
    onCompleted: (result) => {
      if (result.foodItem) setFoodItem(result.foodItem);
      if (result.shef) setShef(result.shef);
    },
  });

  const foodItem = cachedFoodItem ?? data?.foodItem;
  const shef = cachedShef ?? data?.shef;

  if (loading || !foodItem || !shef) {
    return null;
  }

  // Convert the props to match the component interface
  const componentProps: IMealPlanDishCardComponentProps = {
    ...props,
    foodItem,
    shef,
  };

  return <MealPlanDishCardComponent {...componentProps} />;
};
